/* Copyright (c) 2022-2025 Nordcloud Oy or its affiliates. All Rights Reserved. */

import React, { useState } from "react";
import { Else, If, Then, When } from "react-if";
import { CSSObject } from "styled-components";
import { theme, ButtonProps, Button } from "@nordcloud/gnui";
import { isNotNil } from "~/tools";
import { DetailCopyClipboard } from "./DetailCopyClipboard";
import { InlineEdit } from "./InlineEdit";
import { InlineEditProps } from "./InlineEdit/types";
import {
  DetailsDescription,
  DetailsItem,
  DetailsTitle,
  DetailWrapper,
  ValueWrapper,
} from "./styles";
import { isString } from "./utils";

export type DetailData = {
  label?: string;
  value: string | string[] | number | undefined | null;
  placeholder?: string;
  search?: boolean;
  clipboard?: boolean;
  target?: string | null;
  renderComponent?: () => React.JSX.Element | undefined;
  editProps?: Omit<InlineEditProps, "onClose">;
};

type EditButtonSize = "sm" | "md";

type Props = {
  detail: DetailData;
  customCss?: CSSObject;
  editButtonSize?: EditButtonSize;
};

export function Detail({ detail, customCss, editButtonSize }: Props) {
  const [isEditMode, setIsEditMode] = useState(false);

  const clipboardValue = isString(detail.value) ? detail.value : "";

  const editor = detail.editProps && (
    <InlineEdit
      {...detail.editProps}
      onClose={() => setIsEditMode(false)}
      onSuccess={detail.editProps.onSuccess}
      value={detail.editProps?.value ?? detail.value}
      placeholder={detail.placeholder}
    />
  );

  const isEmpty = detail.value == null;

  return (
    <DetailsItem css={customCss} data-testid={detail.label}>
      <If condition={isEditMode}>
        <Then>{editor}</Then>
        <Else>
          <DetailWrapper
            aria-label="details item"
            columnGap={theme.spacing.spacing02}
          >
            <div>
              <When condition={isNotNil(detail.label)}>
                <DetailsTitle>{detail.label}</DetailsTitle>
              </When>
              <DetailsDescription>
                <ValueWrapper
                  editable={detail.editProps !== undefined}
                  isEmpty={isEmpty}
                >
                  <When condition={detail.renderComponent?.() !== undefined} />
                  <If condition={detail.renderComponent?.() !== undefined}>
                    <Then>{detail.renderComponent?.()}</Then>
                    <Else>{detail.value ?? "No data provided"}</Else>
                  </If>
                </ValueWrapper>
              </DetailsDescription>
            </div>
            <When condition={detail.editProps !== undefined}>
              <EditButton
                onClick={() => setIsEditMode(true)}
                editButtonSize={editButtonSize}
              />
            </When>
            <When condition={detail.clipboard && !isEmpty}>
              <DetailCopyClipboard value={clipboardValue} />
            </When>
          </DetailWrapper>
        </Else>
      </If>
    </DetailsItem>
  );
}

type EditButtonProps = {
  editButtonSize?: EditButtonSize;
} & ButtonProps;

function EditButton(props: EditButtonProps) {
  return (
    <Button
      aria-label="edit button"
      icon="edit"
      size={props.editButtonSize}
      severity="low"
      onClick={props.onClick}
      css={{ visibility: "hidden" }}
    />
  );
}
